import React, { useState, useEffect } from 'react';
import { Reveal } from './Reveal';

// A visual interlude between sections: a full-bleed dance band (Degas's "The
// Rehearsal of the Ballet Onstage") with an epigraph, then a slowly drifting,
// edge-fading marquee of public-domain works — players of stringed instruments
// spanning Baroque → Dutch Golden Age → Romantic, plus an Edo-period Japanese
// woodblock beauty with a koto near the center. The row loops seamlessly and
// fades off both borders of the site. All works are CC0 (The Met, Open Access),
// under /images/gallery.
const GALLERY = [
  {
    src: '/images/gallery/hals-boy-with-a-lute.jpg',
    title: 'Boy with a Lute',
    artist: 'Frans Hals',
    date: 'ca. 1625',
  },
  {
    src: '/images/gallery/de-keyser-musician-and-daughter.jpg',
    title: 'A Musician and His Daughter',
    artist: 'Thomas de Keyser',
    date: '1629',
  },
  {
    src: '/images/gallery/vouet-woman-playing-a-guitar.jpg',
    title: 'Woman Playing a Guitar',
    artist: 'Simon Vouet',
    date: 'ca. 1618',
  },
  {
    src: '/images/gallery/toshikata-woman-playing-koto.jpg',
    title: 'Playing the Koto',
    artist: 'Mizuno Toshikata',
    date: '1894',
  },
  {
    src: '/images/gallery/vermeer-young-woman-with-a-lute.jpg',
    title: 'Young Woman with a Lute',
    artist: 'Johannes Vermeer',
    date: 'ca. 1662–63',
  },
  {
    src: '/images/gallery/metsu-a-musical-party.jpg',
    title: 'A Musical Party',
    artist: 'Gabriël Metsu',
    date: '1659',
  },
  {
    src: '/images/gallery/burne-jones-the-love-song.jpg',
    title: 'The Love Song',
    artist: 'Sir Edward Burne-Jones',
    date: '1868–77',
  },
];

type Piece = (typeof GALLERY)[number];

export function Interlude() {
  const [index, setIndex] = useState<number | null>(null);
  const open = index !== null;
  const piece: Piece | null = index !== null ? GALLERY[index] : null;
  const go = (dir: number) => setIndex(i => (i === null ? i : (i + dir + GALLERY.length) % GALLERY.length));

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIndex(null);
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open]);

  return (
    <section className="interlude" aria-label="A visual interlude">
      <div
        className="interlude__band"
        role="img"
        aria-label="The Rehearsal of the Ballet Onstage, Edgar Degas, ca. 1874"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/gallery/degas-rehearsal-of-the-ballet.jpg)` }}
      >
        <div className="interlude__overlay" />
        <Reveal>
          <p className="interlude__epigraph">
            &ldquo;Where words fail, music speaks.&rdquo;
          </p>
        </Reveal>
      </div>

      <Reveal>
        {/* Edge-fading marquee: the track holds the works twice so the
            translateX loop is seamless. Clicks map back to the real index
            via modulo. Auto-scroll pauses on hover and is disabled under
            prefers-reduced-motion (falls back to a scrollable row). */}
        <div className="gallery-marquee" aria-label="A drifting gallery of musical paintings">
          <ul className="gallery-track">
            {[...GALLERY, ...GALLERY].map((g, i) => (
              <li className="gallery__item" key={i} aria-hidden={i >= GALLERY.length}>
                <button
                  type="button"
                  className="gallery__frame"
                  onClick={() => setIndex(i % GALLERY.length)}
                  tabIndex={i >= GALLERY.length ? -1 : 0}
                  aria-label={`View ${g.title} by ${g.artist} larger`}
                >
                  <img src={g.src} alt={`${g.title} — ${g.artist}, ${g.date}`} loading="lazy" />
                </button>
                <figcaption>
                  <span className="gallery__title">{g.title}</span>
                  <span className="gallery__meta">{g.artist} · {g.date}</span>
                </figcaption>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
      <div className="container">
        <p className="gallery__credit">
          Public-domain works · The Metropolitan Museum of Art, Open Access (CC0)
        </p>
      </div>

      {open && piece && (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`${piece.title} — ${piece.artist}`}
          onClick={() => setIndex(null)}
        >
          <button className="lightbox__close" aria-label="Close" onClick={() => setIndex(null)}>×</button>
          <button
            className="lightbox__nav lightbox__nav--prev"
            aria-label="Previous artwork"
            onClick={e => { e.stopPropagation(); go(-1); }}
          >‹</button>
          <figure className="lightbox__inner" onClick={e => e.stopPropagation()}>
            <img src={piece.src} alt={`${piece.title} — ${piece.artist}, ${piece.date}`} />
            <figcaption>
              <span className="lightbox__title">{piece.title}</span>
              <span className="lightbox__meta">{piece.artist} · {piece.date}</span>
            </figcaption>
          </figure>
          <button
            className="lightbox__nav lightbox__nav--next"
            aria-label="Next artwork"
            onClick={e => { e.stopPropagation(); go(1); }}
          >›</button>
        </div>
      )}
    </section>
  );
}
